@file:OptIn(ExperimentalComposeWebApi::class)

package com.siriusxm.unifiedcontent.console.player

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.SVGIcons
import com.siriusxm.pia.components.svgIcon
import com.siriusxm.pia.player.AudioTrack
import com.siriusxm.pia.player.CMCPlayer
import com.siriusxm.pia.player.PlayState
import com.siriusxm.pia.player.PlaybackController
import contentingestion.unifiedmodel.ReferenceIdentifiable
import kotlin.math.roundToInt
import kotlinx.browser.document
import kotlinx.coroutines.flow.StateFlow
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Span
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

/**
 * Manages the state of the global audio player, and provides the public interface to update the
 * control.
 *
 * This is a thin request-shaped facade over a shared headless [AudioPlayerController]: it owns the one
 * long-lived controller that backs the global player bar and forwards `play(...)` requests to it. The
 * bar's [AudioPlayerView] observes the same controller's flows and drives its transport. The controller
 * carries all the engine state (media element, hls.js, credential refresh); this type exists only so
 * existing call sites that hold an `AudioPlayerState` keep working unchanged after the engine was
 * extracted for the headless player library (UCA-1111).
 */
class AudioPlayerState(
    /**
     * The single engine behind the global bar. Never disposed for the app's lifetime — unlike an
     * app-created [CMCPlayer], the global bar's controller lives as long as the
     * app does. Injected so the app can back the bar with a [CMCPlayer]'s
     * controller — sharing one prepare→track→engine path between the bar and the headless facade
     * (UCA-1111) — while callers that want a standalone bar can still let it default.
     */
    val controller: PlaybackController = AudioPlayerController(),
    /** Resolve related presentation data through the host's catalog client, separately from playback. */
    val resolveRelatedEntityName: suspend (ReferenceIdentifiable) -> String = { it.id },
    /** Optional catalog navigation supplied by the host; the player does not own application routes. */
    val relatedEntityHref: (ReferenceIdentifiable) -> String? = { null },
) {

    val state: StateFlow<PlayState> = controller.state
    val track: StateFlow<AudioTrack?> = controller.track

    fun play(url: String, title: String? = null, subTitle: String? = null, imageUrl: String? = null) {
        controller.play(AudioTrack(url, title, subTitle, imageUrl))
    }

    /**
     * Play [track]. Prefer this overload when the stream carries credential-refresh information so the
     * player can renew short-lived CloudFront cookies during long playback (see
     * [com.siriusxm.pia.player.AudioTrack]).
     */
    fun play(track: AudioTrack) {
        controller.play(track)
    }
}

/**
 * Renders the global audio player: a single transport row showing artwork (or a placeholder when the
 * track has none), the title/subtitle, skip ±15s / play-pause transport controls, an elapsed/total
 * time readout, a draggable seek scrubber, a volume control, and a close button. Closing stops
 * playback and unloads the track, which hides the bar — the same effect as the `close()` bridge call
 * an embedded app makes when it starts its own playback (UCA-1133).
 *
 * This is a thin view over the [AudioPlayerState]'s shared [AudioPlayerController]: it observes the
 * controller's state/track/position/duration/volume/error [StateFlow]s and drives playback through the
 * controller's transport methods. The controller (in [AudioPlayerController]) owns the actual media
 * element and hls.js engine, so this composable neither creates nor tears one down — the global bar's
 * controller lives for the app's lifetime.
 */
@Composable
fun AudioPlayerView(state: AudioPlayerState) {
    val controller = state.controller
    val track by controller.track.collectAsState()
    val details by controller.details.collectAsState()
    val playerState by controller.state.collectAsState()
    val currentTime by controller.position.collectAsState()
    val duration by controller.duration.collectAsState()
    val volume by controller.volume.collectAsState()
    val playbackError by controller.playbackError.collectAsState()
    var detailsExpanded by remember(track) { mutableStateOf(false) }
    val relatedNames = remember(track) { mutableMapOf<ReferenceIdentifiable, String>() }

    Style(AudioPlayerStyles)
    val source = track ?: return
    val current = details.current.takeIf { source.loadDetails != null }
    val expanded = detailsExpanded && playbackError == null && source.loadDetails != null
    val failed = playbackError != null
    Div({ classes(AudioPlayerStyles.playerArea) }) {
        if (expanded) {
            PlaybackDetailsPanel(
                details, source, state.resolveRelatedEntityName, state.relatedEntityHref, relatedNames,
                onCollapse = { detailsExpanded = false },
            )
        }
        playbackError?.let { error ->
            PlaybackWarning(error, source.isLive) { controller.play(source) }
        }
        Div({ classes(AudioPlayerStyles.primaryRow) }) {
            Div({
                classes(AudioPlayerStyles.sourceIdentity)
                if (!expanded && !failed && current != null) classes(AudioPlayerStyles.sourceWithDetails)
            }) {
                PlayerArtwork(source.imageUrl, AudioPlayerStyles.previewImage)
                Div({ classes(AudioPlayerStyles.titleArea) }) {
                    MarqueeText(source.title ?: "Audio", AudioPlayerStyles.title)
                    if (!expanded && !failed && current != null) {
                        Div({ classes(AudioPlayerStyles.compactNowPlaying) }) {
                            Span({ classes(AudioPlayerStyles.nowPlayingLabel) }) { Text("NOW PLAYING") }
                            MarqueeText(listOfNotNull(current.title, current.subtitle).joinToString(" — "), AudioPlayerStyles.cutSummary)
                        }
                    } else {
                        Div({ classes(AudioPlayerStyles.sourceCaption) }) { Text(sourceCaption(source)) }
                    }
                }
            }
            Div({ classes(AudioPlayerStyles.transport) }) {
                PlayerIconButton("Back 15 seconds", enabled = !source.isLive && !failed, onClick = {
                    controller.seek((currentTime - 15).coerceAtLeast(0.0))
                }) { svgIcon(SVGIcons.rewindBackward15) { size = 30.px } }
                Button({
                    classes(AudioPlayerStyles.playButton)
                    attr("type", "button")
                    attr("aria-label", if (playerState == PlayState.PLAYING) "Pause" else "Play")
                    if (playerState == PlayState.LOADING) attr("aria-busy", "true")
                    if (failed || playerState == PlayState.LOADING) attr("disabled", "")
                    onClick { if (playerState == PlayState.PLAYING) controller.pause() else controller.resume() }
                }) {
                    if (playerState == PlayState.LOADING) {
                        Div({ classes(AudioPlayerStyles.spinner) })
                    } else {
                        svgIcon(if (playerState == PlayState.PLAYING) SVGIcons.playerPauseFilled else SVGIcons.playerPlayFilled) { size = 26.px }
                    }
                }
                if (!source.isLive) {
                    PlayerIconButton("Forward 15 seconds", enabled = !failed, onClick = {
                        val maximum = if (duration > 0) duration else currentTime + 15
                        controller.seek((currentTime + 15).coerceAtMost(maximum))
                    }) { svgIcon(SVGIcons.rewindForward15) { size = 28.px } }
                }
            }
            Div({ classes(AudioPlayerStyles.seekArea) }) {
                PlayerScrubber(currentTime, duration, interactive = !source.isLive && !failed, label = "Playback position") {
                    controller.seek(it)
                }
            }
            Div({
                classes(AudioPlayerStyles.timeDisplay)
                if (source.isLive && !failed) classes(AudioPlayerStyles.liveStatus)
                if (failed) classes(AudioPlayerStyles.disabledStatus)
            }) {
                if (source.isLive) {
                    Span({ classes(AudioPlayerStyles.liveDot); attr("aria-hidden", "true") })
                    Text("LIVE")
                } else {
                    Text("${formatTime(currentTime)} / ${formatTime(duration)}")
                }
            }
            Div({ classes(AudioPlayerStyles.volumeArea) }) {
                svgIcon(SVGIcons.volume) { size = 24.px }
                PlayerScrubber(volume, 1.0, label = "Volume") { controller.setVolume(it) }
            }
            if (source.loadDetails != null && !expanded) {
                PlayerDetailsToggle(false) { detailsExpanded = true }
            }
            PlayerIconButton("Close player", className = AudioPlayerStyles.closeButton, onClick = { controller.stop() }) {
                svgIcon(SVGIcons.xMark) { size = 26.px }
            }
        }
    }
}

/**
 * A metadata line (track title or subtitle) that ellipsizes when it fits and scrolls horizontally on
 * hover when [text] is too wide for the column. The whole line is measured after layout: when the
 * inner content overflows its viewport the line is "armed" (given [AudioPlayerStyles.marqueeScrollable])
 * and, on hover, the CSS in that class animates the inner element end-to-end and back.
 *
 * The scroll distance and duration are set per-line as CSS custom properties so the travel exactly
 * covers the hidden overflow and the speed stays constant regardless of how long the text is. Text
 * that already fits is never armed, so short titles stay perfectly still on hover. Re-measures whenever
 * [text] changes (a new track) so a previously-scrolling line stops once a shorter title takes its place.
 *
 * [lineClass] carries the font styling (size / weight / opacity) and is applied to the inner element so
 * the moving text keeps the title's or subtitle's appearance.
 */
@Composable
private fun MarqueeText(text: String, lineClass: String) {
    // px/second travel speed for the scroll; distance ÷ speed gives each line its own duration.
    val scrollSpeedPxPerSec = 40.0
    val innerId = remember { "player-marquee-${marqueeIdCounter++}" }

    // Measure after layout (and again whenever the text changes for a new track). When the inner
    // content overflows its viewport, arm the line and set the per-line scroll distance/duration;
    // otherwise leave it un-armed so a short title never scrolls on hover.
    DisposableEffect(text) {
        val inner = document.getElementById(innerId) as? HTMLElement
        val viewport = inner?.parentElement as? HTMLElement
        if (inner != null && viewport != null && inner.scrollWidth > inner.clientWidth + 1) {
            val distance = inner.scrollWidth - inner.clientWidth
            viewport.classList.add(AudioPlayerStyles.marqueeScrollable)
            // Negative translate pulls the tail of the text into view; the keyframes read these.
            inner.style.setProperty("--marquee-end", "${-distance}px")
            inner.style.setProperty("--marquee-duration", "${distance / scrollSpeedPxPerSec}s")
        }
        onDispose {
            viewport?.classList?.remove(AudioPlayerStyles.marqueeScrollable)
            inner?.style?.removeProperty("--marquee-end")
            inner?.style?.removeProperty("--marquee-duration")
        }
    }

    Div({ classes(AudioPlayerStyles.marqueeViewport) }) {
        Div({
            id(innerId)
            classes(AudioPlayerStyles.marqueeInner, lineClass)
        }) {
            Text(text)
        }
    }
}

/**
 * A single-handle scrubber (used for both the seek bar and the volume slider). Renders a track, a
 * filled portion up to [position], and a draggable handle. Dragging or clicking anywhere on the
 * track reports the new value (in the same unit as [total]) via [onSeek]. When [interactive] is
 * false, renders only the background track: no progress fill, handle, or seek/drag interaction.
 * Live playback uses this mode to preserve the same layout as on-demand media.
 *
 * The drag uses document-level mousemove/mouseup listeners while active so the pointer can leave the
 * track without dropping the drag — the same approach as the transcripts TimelineScrubber.
 */
@Composable
private fun PlayerScrubber(
    position: Double,
    total: Double,
    interactive: Boolean = true,
    label: String,
    onSeek: (Double) -> Unit,
) {
    var isDragging by remember { mutableStateOf(false) }
    val containerId = remember { "player-scrubber-${scrubberIdCounter++}" }

    val fraction = if (total > 0) (position / total).coerceIn(0.0, 1.0) else 0.0
    val percent = fraction * 100

    fun valueFromClientX(clientX: Double) {
        val el = document.getElementById(containerId) as? HTMLElement ?: return
        val rect = el.getBoundingClientRect()
        if (rect.width <= 0) return
        val f = ((clientX - rect.left) / rect.width).coerceIn(0.0, 1.0)
        onSeek(f * total)
    }

    LaunchedEffect(isDragging, interactive) {
        if (!interactive) {
            isDragging = false
        } else if (isDragging) {
            val moveHandler: (Event) -> Unit = { event ->
                (event as? MouseEvent)?.let { valueFromClientX(it.clientX.toDouble()) }
            }
            val upHandler: (Event) -> Unit = { isDragging = false }
            document.addEventListener("mousemove", moveHandler)
            document.addEventListener("mouseup", upHandler)
            kotlinx.coroutines.coroutineScope {
                try {
                    kotlinx.coroutines.awaitCancellation()
                } finally {
                    document.removeEventListener("mousemove", moveHandler)
                    document.removeEventListener("mouseup", upHandler)
                }
            }
        }
    }

    Div({
        id(containerId)
        style {
            width(100.percent)
            height(20.px)
            position(Position.Relative)
            property("cursor", if (interactive) "pointer" else "default")
            property("user-select", "none")
        }
        if (interactive) {
            attr("role", "slider")
            attr("aria-label", label)
            attr("aria-valuemin", "0")
            attr("aria-valuemax", total.toString())
            attr("aria-valuenow", position.coerceIn(0.0, total.coerceAtLeast(0.0)).toString())
            tabIndex(0)
            onKeyDown { event ->
                val step = if (total <= 1.0) 0.05 else 5.0
                val value = when (event.key) {
                    "ArrowLeft", "ArrowDown" -> position - step
                    "ArrowRight", "ArrowUp" -> position + step
                    "Home" -> 0.0
                    "End" -> total
                    else -> null
                }
                if (value != null) {
                    event.preventDefault()
                    onSeek(value.coerceIn(0.0, total.coerceAtLeast(0.0)))
                }
            }
            onMouseDown { event ->
                isDragging = true
                event.preventDefault()
                valueFromClientX(event.clientX.toDouble())
            }
        }
    }) {
        // Track background.
        Div({
            style {
                width(100.percent)
                height(4.px)
                position(Position.Absolute)
                top(8.px)
                backgroundColor(SXMUI.playerTrackColor.value())
                borderRadius(2.px)
                property("pointer-events", "none")
            }
        })
        if (interactive) {
            // Saturated progress fill matches the transport accent and volume control.
            Div({
                style {
                    width(percent.percent)
                    height(4.px)
                    position(Position.Absolute)
                    top(8.px)
                    backgroundColor(SXMUI.playerAccentColor.value())
                    borderRadius(2.px)
                    property("pointer-events", "none")
                }
            })
            // Handle.
            Div({
                style {
                    width(14.px)
                    height(14.px)
                    position(Position.Absolute)
                    top(3.px)
                    property("left", "calc($percent% - 7px)")
                    backgroundColor(SXMUI.playerControlSurface.value())
                    borderRadius(50.percent)
                    property("box-shadow", "0 1px 3px ${SXMUI.boxShadowColor.value()}")
                    property("pointer-events", "none")
                }
            })
        }
    }
}

/** Monotonic counter giving each scrubber instance a stable, unique DOM id for hit-testing. */
private var scrubberIdCounter = 0

/** Monotonic counter giving each marquee line a stable, unique DOM id for post-layout measurement. */
private var marqueeIdCounter = 0

/**
 * Format a duration in seconds as `M:SS` (under an hour) or `H:MM:SS` (an hour or more). Non-finite
 * or negative inputs render as `0:00`.
 */
private fun formatTime(seconds: Double): String {
    if (!seconds.isFinite() || seconds < 0) return "0:00"
    val total = seconds.roundToInt()
    val h = total / 3600
    val m = (total % 3600) / 60
    val s = total % 60
    val ss = s.toString().padStart(2, '0')
    return if (h > 0) {
        val mm = m.toString().padStart(2, '0')
        "$h:$mm:$ss"
    } else {
        "$m:$ss"
    }
}
